import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router";
import Layout from "./components/layout/Layout";
import "./index.css";
import VariablesPage from "./pages/variables/VariablesPage";
import VariableDetailPage from "./pages/variables/VariableDetailPage";
import CreateVariablePage from "./pages/variables/CreateVariablePage";
import EditVariablePage from "./pages/variables/EditVariablePage";
import FlowsPage from "./pages/flows/FlowsPage";
import FlowDetailV2Page from "./pages/flows/FlowDetailV2Page";
import CreateFlowPage from "./pages/flows/CreateFlowPage";
import NotFoundPage from "./pages/NotFoundPage";
import TemplatesPage from "./pages/templates/TemplatesPage";
import TemplateDetailPage from "./pages/templates/TemplateDetailPage";
import EditTemplatePage from "./pages/templates/EditTemplatePage";
import ConditionRulesPage from "./pages/condition_rules/ConditionRulesPage";
import EditConditionRulePage from "./pages/condition_rules/EditConditionRulePage";
import ClientsPage from "./pages/clients/ClientsPage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";
import QuietPeriodsPage from "./pages/quiet_periods/QuietPeriodsPage";
import QuietPeriodDetailPage from "./pages/quiet_periods/QuietPeriodDetailPage";
import CreateQuietPeriodPage from "./pages/quiet_periods/CreateQuietPeriodPage";
import EditQuietPeriodPage from "./pages/quiet_periods/EditQuietPeriodPage";
import HttpConfigsPage from "./pages/http/HttpConfigsPage";
import HttpEndpointsPage from "./pages/http/HttpEndpointsPage";
import NodeGroupsPage from "./pages/node_groups/NodeGroupsPage";
import NodeGroupDetailPage from "./pages/node_groups/NodeGroupDetailPage";
import LoginPage from "./pages/auth/LoginPage";
import UsersPage from "./pages/users/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import EditClientPage from "./pages/clients/EditClientPage";
import EditCampaignPage from "./pages/campaigns/EditCampaignPage";
import EditHttpConfigPage from "./pages/http/EditHttpConfigPage";
import EditHttpEndpointPage from "./pages/http/EditHttpEndpointPage";
import ActionsPage from "./pages/ActionsPage";
import EmailActionEditPage from "./pages/actions/EmailActionEditPage";
import SmsActionEditPage from "./pages/actions/SmsActionEditPage";
import RunQueryActionEditPage from "./pages/actions/RunQueryActionEditPage";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <Layout>
              <Routes>
                {/* Default to Flows */}
                <Route path="/" element={<FlowsPage />} />

                {/* Flows routes */}
                <Route path="/flows" element={<FlowsPage />} />
                <Route path="/flows/new" element={<CreateFlowPage />} />
                <Route path="/flows/:scriptId" element={<FlowDetailV2Page />} />

                {/* Variables routes */}
                <Route path="/variables" element={<VariablesPage />} />
                <Route path="/variables/new" element={<CreateVariablePage />} />
                <Route path="/variables/:id" element={<VariableDetailPage />} />
                <Route
                  path="/variables/:id/edit"
                  element={<EditVariablePage />}
                />

                {/* Templates routes */}
                <Route path="/templates" element={<TemplatesPage />} />
                <Route
                  path="/templates/:templateId"
                  element={<TemplateDetailPage />}
                />
                <Route
                  path="/templates/:templateId/edit"
                  element={<EditTemplatePage />}
                />

                {/* Condition Rules */}
                <Route
                  path="/condition-rules"
                  element={<ConditionRulesPage />}
                />
                <Route
                  path="/condition-rules/:id/edit"
                  element={<EditConditionRulePage />}
                />

                {/* Node Groups */}
                <Route path="/node-groups" element={<NodeGroupsPage />} />
                <Route
                  path="/node-groups/:groupId"
                  element={<NodeGroupDetailPage />}
                />

                {/* Actions routes */}
                <Route path="/actions" element={<ActionsPage />} />
                <Route
                  path="/actions/email/:id"
                  element={<EmailActionEditPage />}
                />
                <Route
                  path="/actions/sms/:id"
                  element={<SmsActionEditPage />}
                />
                <Route
                  path="/actions/run_query/:id"
                  element={<RunQueryActionEditPage />}
                />

                {/* Clients routes */}
                <Route path="/clients" element={<ClientsPage />} />
                <Route path="/clients/:id/edit" element={<EditClientPage />} />

                {/* Campaigns routes */}
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route
                  path="/campaigns/:id/edit"
                  element={<EditCampaignPage />}
                />

                {/* Quiet Periods routes */}
                <Route path="/quiet-periods" element={<QuietPeriodsPage />} />
                <Route
                  path="/quiet-periods/new"
                  element={<CreateQuietPeriodPage />}
                />
                <Route
                  path="/quiet-periods/:id"
                  element={<QuietPeriodDetailPage />}
                />
                <Route
                  path="/quiet-periods/:id/edit"
                  element={<EditQuietPeriodPage />}
                />

                {/* HTTP Configs & Endpoints */}
                <Route path="/http-configs" element={<HttpConfigsPage />} />
                <Route
                  path="/http-configs/:id/edit"
                  element={<EditHttpConfigPage />}
                />
                <Route path="/http-endpoints" element={<HttpEndpointsPage />} />
                <Route
                  path="/http-endpoints/:id/edit"
                  element={<EditHttpEndpointPage />}
                />

                {/* Users & Settings */}
                <Route path="/users" element={<UsersPage />} />
                <Route path="/settings" element={<SettingsPage />} />

                {/* 404 route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Layout>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

export default App;
